import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X, Send } from "lucide-react";
import { useApp } from "../../context/formflow/AppContext";
import { ROLE_PALETTE, ACCENT } from "../../constants/formflow/fieldTypes";
import { FIELD_TYPES } from "../../constants/formflow/fieldTypes";
import axiosInstance from "../../api/axiosInstance";

export function SendFormPage() {
  const navigate = useNavigate();
  const { dispatch, showToast } = useApp();
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recipients, setRecipients] = useState([]);

  const update = (idx, patch) =>
    setRecipients((prev) =>
      prev.map((rec, i) => {
        if (i !== idx) return rec;
        const updated = { ...rec, ...patch };
        if ("roleId" in patch) {
          const roleIdNum = Number(patch.roleId);
          updated.roleId = roleIdNum;
          updated.roleName = form.roles.find((r) => r.id === roleIdNum)?.name || "";
        }
        return updated;
      }),
    );

  const handleSend = async () => {
    try {
      if (recipients.some((r) => !r.name.trim() || !r.email.trim())) {
        showToast("Fill in all recipient details", "error");
        return;
      }
      if (recipients.some((r) => !r.email.includes("@"))) {
        showToast("Check email addresses", "error");
        return;
      }
      const resp = await axiosInstance.post("/api/formflow/envelopes", {
        title: form.name,
        formId: form.id,
        fileId: form.file.id,
        recipients,
      });
      const data = resp.data;
      if (data?.success) {
        showToast(`Sent to ${recipients.length} recipient${recipients.length > 1 ? "s" : ""}!`, "success");
        dispatch({
          type: "SEND_FORM",
          payload: {
            formId: form.id,
            formName: form.name,
            roles: form.roles,
            fields: form.fields,
            pdfName: form.pdfName,
            recipients,
          },
        });
        navigate("/formflow");
        return;
      }
      showToast(data?.message || "Failed to send envelope", "error");
    } catch (error) {
      console.error("error send recipients.", error);
      showToast("Server error while sending envelope", "error");
    }
  };

  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/api/formflow/forms/${formId}`);
        const data = res.data;
        if (!data?.success) return;
        const formData = data.form;
        setForm(formData);
        setRecipients(
          formData.roles.map((role) => ({
            roleId: role.id,
            roleName: role.name,
            name: "",
            email: "",
          })),
        );
      } catch (err) {
        showToast("Failed to load form", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchForm();
  }, [formId]);

  if (loading) {
    return <div className="p-6">Loading form...</div>;
  }
  if (!form) return null;

  const getFieldsByRole = (roleId) => {
    return form?.fields?.filter((f) => Number(f.roleId) === Number(roleId)) || [];
  };

  return (
    <div className="bg-slate-50">
      <div className="min-h-screen bg-slate-50 py-7 px-10  max-w-7xl mx-auto">
        <button onClick={() => navigate("/formflow")} className="flex items-center gap-1.5 border-none bg-transparent cursor-pointer text-gray-500 text-sm mb-5 py-1.5">
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <div className="mb-7">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1.5">Send "{form.name}"</h1>
          <p className="text-gray-500 text-sm m-0">Add recipients for each role — they'll get a link to fill their assigned fields.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-7">
          <div>
            <div className="flex justify-between items-center mb-3.5">
              <h2 className="text-lg font-bold text-gray-900 m-0">Recipients</h2>
              <button
                onClick={() =>
                  setRecipients((r) => [
                    ...r,
                    {
                      roleId: form.roles[0]?.id ?? null,
                      roleName: form.roles[0]?.name ?? "",
                      name: "",
                      email: "",
                    },
                  ])
                }
                className="flex items-center gap-1.5 py-1.5 px-3.5 border border-gray-200 rounded-lg bg-white cursor-pointer text-[13px] font-medium text-gray-700"
              >
                <Plus size={14} /> Add More
              </button>
            </div>
            <div className="flex flex-col gap-3.5">
              {recipients.map((rec, idx) => {
                const roleIndex = form.roles.findIndex((r) => r.id === rec.roleId);
                const c = ROLE_PALETTE[Math.max(0, roleIndex) % ROLE_PALETTE.length];
                return (
                  <div key={idx} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <div className="flex justify-between items-center mb-3.5">
                      <div className="inline-flex items-center gap-1.5 py-1 px-3 rounded-full" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: c.dot }} />
                        <select value={rec.roleId} onChange={(e) => update(idx, { roleId: e.target.value })} className="border-none outline-none bg-transparent text-[13px] font-semibold cursor-pointer" style={{ color: c.text }}>
                          {form.roles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {recipients.length > 1 && (
                        <button onClick={() => setRecipients((r) => r.filter((_, i) => i !== idx))} className="bg-transparent border-none cursor-pointer text-gray-300 flex p-1">
                          <X size={15} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        ["Full Name", "name", "John Smith", "text"],
                        ["Email", "email", "john@example.com", "email"],
                      ].map(([lbl, key, ph, tp]) => (
                        <div key={key}>
                          <label className="block text-xs text-gray-500 font-medium mb-1.5">{lbl}</label>
                          <input type={tp} value={rec[key]} onChange={(e) => update(idx, { [key]: e.target.value })} placeholder={ph} className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-[13px] outline-none box-border text-gray-900" />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              onClick={handleSend}
              className="mt-5 w-full py-3.5 text-white border-none rounded-xl cursor-pointer text-[15px] font-semibold flex items-center justify-center gap-2.5"
              style={{
                background: ACCENT,
                boxShadow: "0 4px 14px rgba(220,38,38,0.3)",
              }}
            >
              <Send size={17} /> Send to {recipients.length} Recipient{recipients.length !== 1 ? "s" : ""}
            </button>
          </div>

          <div>
            <h3 className="text-[15px] font-bold text-gray-900 m-0 mb-3.5">Fields per Role</h3>
            <div className="flex flex-col gap-2.5">
              {form.roles.map((role, idx) => {
                const c = ROLE_PALETTE[idx % ROLE_PALETTE.length];
                const flds = getFieldsByRole(role.id);
                return (
                  <div key={role.id} className="bg-white rounded-xl p-3.5 shadow-sm" style={{ border: `1px solid ${c.border}22` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: c.dot }} />
                      <span className="text-[13px] font-bold text-gray-900 flex-1">{role.name}</span>
                      <span className="text-xs text-gray-400">{flds.length} fields</span>
                    </div>
                    {flds.length === 0 ? (
                      <p className="text-xs text-gray-300 m-0">No fields assigned</p>
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        {flds.map((f) => {
                          const ft = FIELD_TYPES.find((t) => t.id === f.type);
                          return (
                            <div key={f.id} className="flex justify-start items-center gap-1 text-sm text-gray-500">
                              <div className="flex gap-5 items-center justify-center">
                                {ft ? <ft.Icon size={13} color="#9ca3af" /> : <></>}
                                {f.label}
                              </div>
                              {f.required ? (
                                <span className="text-lg" style={{ color: ACCENT }}>
                                  *
                                </span>
                              ) : (
                                <></>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
